import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { AsyncPipe } from '@angular/common';

import { catchError, combineLatest, distinctUntilChanged, exhaustMap, filter, map, of, switchMap, take, tap } from 'rxjs';

import { ButtonModule } from '@openng/optimus-ui/button';
import { MessageService } from '@openng/optimus-ui/api';
import { ToastModule } from '@openng/optimus-ui/toast';
import { ConfirmDialogModule } from '@openng/optimus-ui/confirmdialog';
import { CardModule } from '@openng/optimus-ui/card';
import { ProgressSpinnerModule } from '@openng/optimus-ui/progressspinner';
import { DividerModule } from '@openng/optimus-ui/divider';

import { UpdateService } from 'src/app/services/update.service';
import { AuthService } from 'src/app/services/auth.service';
import { environment } from 'src/environments/environment';
import { ApolloClientService } from 'src/app/services/apollo-client.service';
import { AuthenticationMutations } from 'src/app/graphql/mutations/authentication.mutations';
import { GraphqlUserDataOutput } from 'src/app/graphql/models/output/user-data.output.model';
import { convertGraphqlUserDataOutputToUserData } from 'src/app/graphql/mappers/user-data.mappers';
import { HeaderComponent } from 'src/app/components/header/header.component';
import { LaunchDarklyService } from 'src/app/services/launchdarkly.service';
import { LaunchDarklyFlags } from 'src/environments/environment.interfaces';
import { UnderMaintenanceComponent } from 'src/app/components/under-maintenance/under-maintenance.component';
import { ThemeService } from 'src/app/services/theme.service';

/**
 * Main application entry.
 */
@Component({
	imports: [RouterOutlet, ButtonModule, ToastModule, HeaderComponent, AsyncPipe, UnderMaintenanceComponent, ConfirmDialogModule, CardModule, ProgressSpinnerModule, DividerModule],
	selector: 'app-root',
	styleUrl: './app.component.scss',
	templateUrl: './app.component.html',
})
export class AppComponent implements OnInit {
	protected readonly title = signal('chimera');

	private updateService = inject(UpdateService);

	private messageService = inject(MessageService);

	private destroyRef = inject(DestroyRef);

	private authService = inject(AuthService);

	private apolloClientService = inject(ApolloClientService);

	private launchDarklyService = inject(LaunchDarklyService);

	private themeService = inject(ThemeService);

	private router = inject(Router);

	private route = inject(ActivatedRoute);

	private navigationEnded$ = this.router.events.pipe(filter(e => e instanceof NavigationEnd));

	public isProcessingAuthentication = signal<boolean>(false);

	public underMaintenance$ = this.launchDarklyService.launchDarklyFlags$.pipe(map(flags => flags[LaunchDarklyFlags.UnderMaintenance]));

	public supportUrl = environment.supportUrl;

	public wikiUrl = environment.wikiUrl;

	/**
	 * Track application updates.
	 */
	ngOnInit(): void {
		this.themeService.getTheme();

		combineLatest({
			userData: this.authService.userData$,
			navigationEnded: this.navigationEnded$.pipe(take(1)),
		})
			.pipe(
				map(source => source.userData),
				distinctUntilChanged((prev, curr) => {
					// Treat all falsy emissions as equal.
					if (!curr.sessionID && !prev.sessionID) {
						return true;
					}
					return prev.sessionID === curr.sessionID;
				}),
				exhaustMap(data => {
					if (!data.sessionID) {
						const token = this.route.snapshot.queryParamMap.get(environment.oneTimeTokenName);
						if (token) {
							if (token === 'noAuth') {
								this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to sign in. No authentication token provided..' });
								return of(false);
							} else {
								this.isProcessingAuthentication.set(true);
								return this.apolloClientService.authLayerClient.pipe(
									take(1),
									switchMap(client =>
										client.mutate<{ consumeOneTimeToken: GraphqlUserDataOutput }>({
											mutation: AuthenticationMutations.consumeOneTimeToken,
											variables: { token: token },
										}),
									),
									map(result => (result.data?.consumeOneTimeToken ? convertGraphqlUserDataOutputToUserData(result.data.consumeOneTimeToken) : undefined)),
									map(data => {
										if (data?.sessionID) {
											this.authService.updateSession(data);
											return false;
										} else {
											this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to retrieve user data. Please sign in again.' });
											return false;
										}
									}),
									catchError(() => {
										this.isProcessingAuthentication.set(false);
										this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to process sign in. Please sign in again.' });
										return of(false);
									}),
								);
							}
						} else {
							return of(true);
						}
					}
					return of(false);
				}),
				tap(() => this.authService.removeSpecificQueryParams(['sessionId', 'username', 'company', environment.oneTimeTokenName])),
				tap(() => this.isProcessingAuthentication.set(false)),

				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe();

		// Check for updates, and reload page to ensure latest client version is installed if necessary.
		this.updateService
			.updatesAvailable$()
			.pipe(
				distinctUntilChanged(),
				tap(updateAvailable => {
					if (updateAvailable) {
						this.showUpdateNotice();
					}
				}),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe();
	}

	/**
	 * Shows a custom toast message that prompts for reloading when a new version is found.
	 */
	public showUpdateNotice() {
		this.messageService.add({
			key: 'reload',
			life: 20000,
		});
	}
}
